import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@certa/catalyst/components/Button";
import { Dialog } from "@certa/catalyst/components/Dialog";
import {
  Typography,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { Stack } from "@certa/catalyst/layouts/Stack";

import { FILTER_TYPES, TableUnified, useTableFilters } from "@certa/datagrid";

import { Plus } from "@certa/icons/components/Plus";

import { useGetDelegationRequests } from "@certa/queries/queries/massDelegation/massDelegation.query";

import {
  DelegateTasks,
  type TransformedDelegationRequest
} from "./DelegateTasks/DelegateTasks";
import { useManageDelegationColumns } from "./ManageDelegationColumns";

const PAGE_SIZE = 10;

type ManageDelegationsProps = {
  canShow: boolean;
  onClose: () => void;
};

export const DELEGATION_REQUEST_FILTERS = [
  {
    name: "search",
    type: FILTER_TYPES.TEXT
  },
  {
    name: "delegate",
    type: FILTER_TYPES.TEXT
  },
  {
    name: "status",
    type: FILTER_TYPES.SINGLE_SELECT
  }
] as const;

export const ManageDelegations = (props: ManageDelegationsProps) => {
  const { canShow, onClose } = props;

  const intl = useIntl();

  const [canShowDelegateTasks, setCanShowDelegateTasks] = useState(false);
  const [selectedDelegationRequest, setSelectedDelegationRequest] =
    useState<TransformedDelegationRequest>();

  const { filters, onFilterChange, currentPage, setCurrentPage } =
    useTableFilters({
      filters: {
        columns: DELEGATION_REQUEST_FILTERS
      }
    });

  const {
    data: delegationRequestData,
    isLoading: isDelegationRequestsLoading
  } = useGetDelegationRequests({
    page: currentPage,
    page_size: PAGE_SIZE,
    status: filters.status,
    user_email: filters.delegate,
    search: filters.search
  });

  const totalCount = delegationRequestData?.count;

  const delegationRequests = delegationRequestData?.results ?? [];

  const delegationRequestsData = delegationRequests.map(delegationRequest => ({
    id: delegationRequest.id,
    name: delegationRequest.name,
    delegate: delegationRequest.user.email,
    startDate: delegationRequest.startDate,
    endDate: delegationRequest.endDate,
    reason: delegationRequest.reason,
    isActive: delegationRequest.isActive,
    status: delegationRequest.status
  }));

  const onCloseDelegateTasks = () => {
    setSelectedDelegationRequest(undefined);
    setCanShowDelegateTasks(false);
  };

  const onCreationSuccess = () => {
    onCloseDelegateTasks();
    setCurrentPage(1);
  };

  const toggleShowDelegateTasks = () => {
    setCanShowDelegateTasks(prevState => !prevState);
  };

  const handleEditDelegationRequest = (
    delegationRequest: TransformedDelegationRequest
  ) => {
    setSelectedDelegationRequest(delegationRequest);
    setCanShowDelegateTasks(true);
  };

  const columns = useManageDelegationColumns({
    onEditDelegationRequest: handleEditDelegationRequest,
    onFilterChange
  });

  const title = intl.formatMessage({
    id: "tasks.delegations.title",
    defaultMessage: "Delegations"
  });

  const description = intl.formatMessage({
    id: "tasks.delegations.description",
    defaultMessage:
      "Delegate tasks to another user while you’re away. Delegates can complete tasks they have access to on your behalf."
  });

  const delegationPeriods = intl.formatMessage({
    id: "tasks.delegations.delegationPeriods",
    defaultMessage: "Delegation Periods"
  });

  const newDelegation = intl.formatMessage({
    id: "tasks.delegations.newDelegation",
    defaultMessage: "New Delegation"
  });

  const caption = intl.formatMessage({
    id: "tasks.delegations.caption",
    defaultMessage: "Delegations"
  });

  return (
    <>
      <Dialog
        show={canShow}
        width="70%"
        maxHeight="60%"
        height="100%"
        onClose={onClose}
        preventCloseOnClickOutside={false}
        title={title}
        description={description}
      >
        <Stack direction="vertical" gap="8" height="100%">
          <Stack
            padding="4 0 8 0"
            direction="horizontal"
            justify="space-between"
            align="center"
            height="fit-content"
          >
            <Typography variant={TypographyVariants.BODY_BOLD}>
              {delegationPeriods}
            </Typography>
            <Button
              size="Small"
              variant="Outline"
              leftIcon={<Plus />}
              onClick={toggleShowDelegateTasks}
            >
              {newDelegation}
            </Button>
          </Stack>
          <Stack overflow="auto" height="100%">
            <TableUnified
              height="100vh"
              caption={caption}
              columns={columns}
              data={delegationRequestsData}
              isLoading={isDelegationRequestsLoading}
              pagination={{
                isManual: true,
                state: { pageIndex: currentPage, pageSize: PAGE_SIZE },
                totalCount: totalCount,
                onChange: setCurrentPage
              }}
            />
          </Stack>
        </Stack>
      </Dialog>
      {canShowDelegateTasks && (
        <DelegateTasks
          canShow={canShowDelegateTasks}
          onClose={onCloseDelegateTasks}
          selectedDelegationRequest={selectedDelegationRequest}
          onCreationSuccess={onCreationSuccess}
        />
      )}
    </>
  );
};
